import { useEffect, useRef } from 'react'
import { useSnackbar } from 'notistack'
import { RootState, useDispatch, useSelector } from 'redux/store'
import { setNoError } from 'redux/slices/createError'

/**
 * Global listener component that watches Redux error state
 * and displays error notifications as toast messages
 * Shows only the first error in a batch of rapid errors
 */
export default function ErrorNotificationListener() {
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const error = useSelector((state: RootState) => state?.createError?.error)
  const errorShownRef = useRef<boolean>(false)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (error && error?.statusCode) {
      // Clear any pending debounce
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }

      // Show notification only if we haven't shown one in this error batch
      if (!errorShownRef.current) {
        const errorMessage = error?.description || 'An error occurred'
        
        enqueueSnackbar(errorMessage, {
          variant: 'error',
          autoHideDuration: 6000
        })
        
        errorShownRef.current = true
      }
      
      // Debounce clearing the error to batch multiple rapid errors
      debounceTimeoutRef.current = setTimeout(() => {
        errorShownRef.current = false
        dispatch(setNoError())
        debounceTimeoutRef.current = null
      }, 500)
    }

    // Cleanup timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [error, enqueueSnackbar, dispatch])

  return null
}

